import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { gsap } from "gsap";
import userAPI from "../../api/UserApi";
import { showError, showSuccess } from "../../utils/toastUtils";
import useAlphabeticInput from "../../hooks/useAlphabeticInput";
import { setAccessToken } from "../../api/axiosInstance";
import { handleApiValidationError } from "../../utils/apiErrorHandler";


// ─── SVG Icons ───────────────────────────────────────────────────────────────
const IconMail = () => (
  <svg className="register-input-icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M2.5 5.5l7.5 5.5 7.5-5.5" strokeLinecap="round" strokeLinejoin="round" />
    <rect x="1" y="4" width="18" height="12" rx="2" strokeLinejoin="round" />
  </svg>
);

const IconLock = () => (
  <svg className="register-input-icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="4" y="9" width="12" height="9" rx="2" />
    <path d="M7 9V6a3 3 0 0 1 6 0v3" strokeLinecap="round" />
    <circle cx="10" cy="13.5" r="1" fill="currentColor" />
  </svg>
);

const IconUser = () => (
  <svg className="register-input-icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="10" cy="7" r="3.5" />
    <path d="M2.5 17c0-3.314 3.358-6 7.5-6s7.5 2.686 7.5 6" strokeLinecap="round" />
  </svg>
);

const IconEye = ({ show }) =>
  show ? (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M1 10s3.5-7 9-7 9 7 9 7-3.5 7-9 7-9-7-9-7z" />
      <circle cx="10" cy="10" r="3" />
    </svg>
  ) : (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M3 3l14 14M8.5 8.6A3 3 0 0 0 10 13a3 3 0 0 0 3-3 3 3 0 0 0-.6-1.8" />
      <path d="M6.1 6.2C3.9 7.5 2 10 2 10s3 6 8 6a8.2 8.2 0 0 0 3.9-1M10 4c5 0 8 6 8 6s-.8 1.6-2.2 3" />
    </svg>
  );

// Decorative SVG watermark
const MandalaWatermark = () => (
  <svg className="register-visual-watermark" viewBox="0 0 400 400" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="200" cy="200" r="190" stroke="white" strokeWidth="1" />
    <circle cx="200" cy="200" r="150" stroke="white" strokeWidth="0.5" />
    <circle cx="200" cy="200" r="110" stroke="white" strokeWidth="1" />
    <circle cx="200" cy="200" r="70" stroke="white" strokeWidth="0.5" />
    <circle cx="200" cy="200" r="30" stroke="white" strokeWidth="1" />
    {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((angle, i) => {
      const rad = (angle * Math.PI) / 180;
      const x1 = 200 + 30 * Math.cos(rad);
      const y1 = 200 + 30 * Math.sin(rad);
      const x2 = 200 + 190 * Math.cos(rad);
      const y2 = 200 + 190 * Math.sin(rad);
      return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="white" strokeWidth="0.5" />;
    })}
    {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => {
      const rad = (angle * Math.PI) / 180;
      const cx = 200 + 150 * Math.cos(rad);
      const cy = 200 + 150 * Math.sin(rad);
      return <circle key={i} cx={cx} cy={cy} r="8" stroke="white" strokeWidth="0.8" />;
    })}
    {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => {
      const rad = (angle * Math.PI) / 180;
      const cx = 200 + 110 * Math.cos(rad);
      const cy = 200 + 110 * Math.sin(rad);
      return <circle key={i} cx={cx} cy={cy} r="5" stroke="white" strokeWidth="0.6" />;
    })}
    <polygon points="200,10 210,190 200,200 190,190" stroke="white" strokeWidth="0.5" fill="none" />
    <polygon points="200,390 210,210 200,200 190,210" stroke="white" strokeWidth="0.5" fill="none" />
    <polygon points="10,200 190,190 200,200 190,210" stroke="white" strokeWidth="0.5" fill="none" />
    <polygon points="390,200 210,190 200,200 210,210" stroke="white" strokeWidth="0.5" fill="none" />
  </svg>
);

// Corner ornament SVG
const CornerOrnament = () => (
  <svg className="register-corner-ornament" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 2 L62 2 L62 62" stroke="white" strokeWidth="1.5" fill="none" />
    <path d="M2 2 L2 62 L62 62" stroke="white" strokeWidth="0.5" fill="none" strokeDasharray="4 4" />
    <circle cx="32" cy="32" r="8" stroke="white" strokeWidth="1" />
    <line x1="32" y1="2" x2="32" y2="24" stroke="white" strokeWidth="0.8" />
    <line x1="32" y1="40" x2="32" y2="62" stroke="white" strokeWidth="0.8" />
    <line x1="2" y1="32" x2="24" y2="32" stroke="white" strokeWidth="0.8" />
    <line x1="40" y1="32" x2="62" y2="32" stroke="white" strokeWidth="0.8" />
  </svg>
);

// ─── Main Component ───────────────────────────────────────────────────────────
export default function Register() {
  useAlphabeticInput();

  const INITIAL_FORM_DATA = {
    email: "",
    password: "",
    firstName: "",
    lastName: "",
    confirmPassword: "",
  };

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();

  // Refs for GSAP
  const visualPanelRef = useRef(null);
  const visualContentRef = useRef(null);
  const cardRef = useRef(null);
  const formGroupRefs = useRef([]);
  const btnRef = useRef(null);
  const loginRowRef = useRef(null);

  const addFormGroupRef = (el) => {
    if (el && !formGroupRefs.current.includes(el)) {
      formGroupRefs.current.push(el);
    }
  };

  // ── GSAP Entrance Animation (same as Login) ──
  useEffect(() => {
    if (typeof gsap === "undefined") return;

    const tl = gsap.timeline({ defaults: { ease: "power3.out" } });

    tl.fromTo(
      visualPanelRef.current,
      { clipPath: "inset(0 100% 0 0)" },
      { clipPath: "inset(0 0% 0 0)", duration: 1.1 },
      0
    );

    tl.fromTo(
      visualContentRef.current,
      { opacity: 0, y: 30 },
      { opacity: 1, y: 0, duration: 0.9 },
      0.45
    );

    tl.to(cardRef.current, { opacity: 1, y: 0, duration: 0.85 }, 0.2);

    tl.to(
      formGroupRefs.current,
      { opacity: 1, y: 0, duration: 0.55, stagger: 0.08 },
      0.55
    );

    tl.to(btnRef.current, { opacity: 1, y: 0, duration: 0.45 }, 0.95);
    tl.to(loginRowRef.current, { opacity: 1, duration: 0.4 }, 1.1);
  }, []);

  // ── Validation (from RegisterCanvas) ──
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const validate = () => {
    const newErrors = {};
    const isValidEmail = emailRegex.test(formData.email);
    const isValidPassword = formData.password.length >= 6;

    if (!formData.firstName) {
      newErrors.firstName = "Please Enter First Name";
    }

    if (!formData.email) {
      newErrors.email = "Please Enter Email Address";
    } else if (!isValidEmail) {
      newErrors.email = "Please enter a valid email";
    }

    if (!formData.password) {
      newErrors.password = "Enter Password";
    } else if (!isValidPassword) {
      newErrors.password = "Password must be at least 6 characters";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Enter Confirm Password";
    } else if (formData.confirmPassword.length < 6) {
      newErrors.confirmPassword = "Confirm Password must be at least 6 characters";
    }

    if (
      formData.password &&
      formData.confirmPassword &&
      formData.password !== formData.confirmPassword
    ) {
      newErrors.general = "Password & Confirm Password must be same";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ── Handle Input Change ──
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear individual field error on change
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  // ── Handle Submit (from RegisterCanvas) ──
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setIsSubmitting(true);

    try {
      const res = await userAPI.register(formData);
      if (res.code === 200) {
        showSuccess(res.message);
        setAccessToken(res.data.access_token);

        const user = {
          email: res.data.email,
          username: res.data.username,
        };

        localStorage.setItem("token", JSON.stringify(user));
        localStorage.setItem("accessToken", res.data.accessToken);

        navigate("/users/dashboard");
      }
    } catch (err) {
      handleApiValidationError(err, setErrors);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="register-page-wrapper">

        {/* ── LEFT: Editorial Visual Panel ── */}
        <div className="register-visual-panel" ref={visualPanelRef}>
          <div className="register-visual-bg" />
          <MandalaWatermark />
          <div className="register-visual-overlay" />
          <CornerOrnament />

          <div className="register-visual-content" ref={visualContentRef}>
            <div className="register-brand-logo">BEGUM'S</div>

            <div className="register-tagline-eyebrow">Heritage · Craft · Legacy</div>

            <h2 className="register-tagline-headline">
              Begin Your<br />
              <em>Journey</em>
            </h2>

            <div className="register-visual-rule" />

            <p className="register-tagline-sub">
              Join a community that celebrates artisanal craft — 
              where every thread tells a story of timeless elegance.
            </p>
          </div>
        </div>

        {/* ── RIGHT: Register Form Panel ── */}
        <div className="register-form-panel">
          <div className="register-card" ref={cardRef}>

            {/* Eyebrow */}
            <div className="register-card-eyebrow">
              <div className="register-card-eyebrow-line" />
              <span className="register-card-eyebrow-text">Create Account</span>
              <div className="register-card-eyebrow-line" />
            </div>

            {/* Title */}
            <h1 className="register-title">Register</h1>
            <p className="register-subtitle">Enter your details to create your account</p>

            {/* Form */}
            <form onSubmit={handleSubmit} noValidate>

              {/* First Name + Last Name Row */}
              <div className="register-form-group register-form-row" ref={addFormGroupRef}>
                {/* First Name */}
                <div>
                  <label htmlFor="register-firstName" className="register-label">
                    First Name <span style={{ color: "#dc3545" }}>*</span>
                  </label>
                  <div className="register-input-wrapper">
                    <input
                      id="register-firstName"
                      type="text"
                      name="firstName"
                      className={`register-input${errors.firstName ? " is-error" : ""}`}
                      placeholder="First Name"
                      value={formData.firstName}
                      onChange={handleChange}
                      autoComplete="given-name"
                    />
                    <IconUser />
                  </div>
                  {errors.firstName && (
                    <span className="register-error-text">{errors.firstName}</span>
                  )}
                </div>

                {/* Last Name */}
                <div>
                  <label htmlFor="register-lastName" className="register-label">
                    Last Name
                  </label>
                  <div className="register-input-wrapper">
                    <input
                      id="register-lastName"
                      type="text"
                      name="lastName"
                      className={`register-input${errors.lastName ? " is-error" : ""}`}
                      placeholder="Last Name"
                      value={formData.lastName}
                      onChange={handleChange}
                      autoComplete="family-name"
                    />
                    <IconUser />
                  </div>
                  {errors.lastName && (
                    <span className="register-error-text">{errors.lastName}</span>
                  )}
                </div>
              </div>

              {/* Email */}
              <div className="register-form-group" ref={addFormGroupRef}>
                <label htmlFor="register-email" className="register-label">
                  Email Address <span style={{ color: "#dc3545" }}>*</span>
                </label>
                <div className="register-input-wrapper">
                  <input
                    id="register-email"
                    type="email"
                    name="email"
                    className={`register-input${errors.email ? " is-error" : ""}`}
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    autoComplete="email"
                  />
                  <IconMail />
                </div>
                {errors.email && (
                  <span className="register-error-text">{errors.email}</span>
                )}
              </div>

              {/* Password + Confirm Password Row */}
              <div className="register-form-group register-form-row" ref={addFormGroupRef}>
                {/* Password */}
                <div>
                  <label htmlFor="register-password" className="register-label">
                    Password <span style={{ color: "#dc3545" }}>*</span>
                  </label>
                  <div className="register-input-wrapper">
                    <input
                      id="register-password"
                      type={showPassword ? "text" : "password"}
                      name="password"
                      className={`register-input${errors.password ? " is-error" : ""}`}
                      placeholder="Password"
                      value={formData.password}
                      onChange={handleChange}
                      autoComplete="new-password"
                    />
                    <IconLock />
                    <button
                      type="button"
                      className="register-password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      <IconEye show={showPassword} />
                    </button>
                  </div>
                  {errors.password && (
                    <span className="register-error-text">{errors.password}</span>
                  )}
                </div>

                {/* Confirm Password */}
                <div>
                  <label htmlFor="register-confirmPassword" className="register-label">
                    Confirm <span style={{ color: "#dc3545" }}>*</span>
                  </label>
                  <div className="register-input-wrapper">
                    <input
                      id="register-confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      className={`register-input${errors.confirmPassword ? " is-error" : ""}`}
                      placeholder="Confirm Password"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      autoComplete="new-password"
                    />
                    <IconLock />
                    <button
                      type="button"
                      className="register-password-toggle"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    >
                      <IconEye show={showConfirmPassword} />
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <span className="register-error-text">{errors.confirmPassword}</span>
                  )}
                </div>
              </div>

              {/* General Error (password mismatch etc.) */}
              {errors.general && (
                <span className="register-general-error">{errors.general}</span>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                className="register-btn-primary"
                ref={btnRef}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Registering..." : "Register"}
              </button>

            </form>

            {/* Login Redirect */}
            <div className="register-login-row" ref={loginRowRef}>
              <span className="register-login-text">
                Have an account?
                <Link to="/users/login" className="register-login-link">
                  Login
                </Link>
              </span>
            </div>

          </div>
        </div>

      </div>
    </>
  );
}